import React, { useEffect, useMemo, useState } from "react";

import Checkbox from "../../utilities/element/Checkbox";
import RangeValue from "../../utilities/element/RangeValue";
import { utilityFns } from "../../utilities/utility-functions";
import RateCalculator from "./rates";
import ToggleButtonGroup from "../../utilities/element/ToggleButtonGroup";
import { getBundleName } from "./helpers";
import {
  addIncludedFeedsToMarkets,
  buildMutualExclusivityGraph,
  collectBundleIncludedMarketIds,
  collectIncludedMarketIdsFromSelections,
  filterMarketdataprovidersForTradingPlatform,
  findProviderByGroupAndName,
  isExclusiveBlockedForFeed,
  normalizeConsistsOfPackageSelections,
  removeMarketSelection,
  removeMutuallyExclusiveSelections,
} from "./market-data-selection";
import ContinueOnDevice from "../../utilities/element/ContinueOnDevice";

import "./business-deal.scss";

const limits = {
  performanceBond: {
    min: 5000,
    default: 5000,
    max: 200000,
  },
  initialBuyingPower: {
    min: 500000,
    default: 500000,
    max: 10000000,
  },
  maximumDailyLoss: {
    min: 700,
    default: 1,
    max: 27000,
  },
  approvedOvernightBuyingPower: {
    min: 0,
    default: 20000,
    max: 2000000,
  },
};
const SHARES = "shares";
const PAYOUT = "payout";
const CUSTOM = "Custom";
const BASIC = "Basic";

// Add tooltip text configurations
const tooltipTexts = {
  zimtraelite: {
    dailyBuyingPower: `Buying power is limited by a *maximum position exposure* to manage risk.

Elite Accounts get 10:1 buying power with a 25% exposure limit - allowing 2.5x on a single trade, or full buying power across four.

Short selling is limited to 2:1 buying power on stocks over $1 — and only accounts with $15,000+ can short stocks under $1.

Learn [more](https://www.youtube.com/shorts/dstd4q-hOPg).`,
    approvedOvernightBuyingPower:
      "Only accounts over $10,000 can hold positions overnight using up to 2:1 buying power.",
  },
  zimtrapro: {
    dailyBuyingPower: `Buying power is limited by a *maximum position exposure* to manage risk.

Pro Accounts get 4:1 buying power with a 25% exposure limit — allowing 2.5x on a single trade, or full buying power across four.

Short selling is limited to 2:1 buying power on stocks over $1 — and only accounts with $15,000+ can short stocks under $1.`,
    approvedOvernightBuyingPower:
      "Only accounts over $10,000 can hold positions overnight using up to 2:1 buying power.",
  },
};

const BusinessDealStep = ({ localState, setLocalState, stepsData }) => {
  const { bundleDescription, rates, profile, tradingPlatform, volumePayments } =
    localState;

  const [bundlePacks, setBundlePacks] = useState([]);

  const TPRates = profile?.accountType
    ? rates?.tradingplatforms.filter((tp) =>
        tp.accountTypeRefs.includes(profile.accountType)
      )
    : rates?.tradingplatforms;

  // Helper function to find and update trading platform based on profile
  const updateTradingPlatform = (profile, rates, localState) => {
    const foundTP = utilityFns.findCurrentTradingPlatform(profile, rates);
    if (foundTP) {
      localState.tradingPlatform = foundTP;
      localState.profile.software = foundTP.software;
      localState.profile.softwareType = foundTP.softwareType;
    } else {
      localState.tradingPlatform = {};
    }

    return { ...localState };
  };

  // Helper function to update profile information based on account type and calculation rates
  const updateProfileWithRates = (accountTypeRef, rates, localState) => {
    const currentCR = rates.calculationrates.find(
      (r) => r.accountTypeRef === accountTypeRef
    );

    if (currentCR) {
      let rulesToSet = {};

      const { buyingPower, performanceBond, stopLoss, overnightBuyingPower } =
        currentCR;

      rulesToSet = {
        initialBuyingPower: buyingPower.min,
        performanceBond: performanceBond.min,
        maximumDailyLoss: stopLoss.min,
        approvedOvernightBuyingPower: overnightBuyingPower.min,
      };

      localState.profile.agreement = {
        ...localState.profile.agreement,
        rules: rulesToSet,
      };

      localState.profile.accountType = accountTypeRef;
      localState.profile.marketdataBundle = BASIC;
    }

    return { ...localState };
  };

  const getCurrentMarketBundle = (localState2) => {
    const mdp = rates?.marketdataproviders?.filter((p) => {
      return p.tradingPlatformRefs.includes(
        utilityFns.findCurrentTradingPlatform(profile, rates)?.reference
      );
    });
    return getBundleName({
      marketdataProviders: mdp,
      currentAccount: localState2.profile,
      tradingPlatforms: rates?.tradingplatforms,
      marketBundles: rates.bundles,
    });
  };

  const updateMarketDataEntitlements = (profile, localState2) => {
    const fromMarketObjects = (marketObjects) => {
      return marketObjects.map((obj) => `${obj.group}-${obj.market}`);
    };
    if (profile.agreement.marketData) {
      const unitedMarkets = fromMarketObjects(profile.agreement.marketData);
      localState2.profile.markets = [...unitedMarkets];
      localState2.profile.marketdataBundle =
        getCurrentMarketBundle(localState2);
      return { ...localState2 };
    }
  };

  const updateMDEntitlementsWIthBackInfo = (profile, localState2) => {
    const providers = localState2.rates?.marketdataproviders || [];
    const tradingPlatformRef =
      localState2.tradingPlatform?.reference ||
      utilityFns.findCurrentTradingPlatform(
        localState2.profile,
        localState2.rates
      )?.reference;
    const providersForPlatform = filterMarketdataprovidersForTradingPlatform(
      providers,
      tradingPlatformRef
    );

    const includedFeedsSet =
      profile.markets && providersForPlatform.length > 0
        ? collectIncludedMarketIdsFromSelections(
            profile.markets,
            providersForPlatform,
            utilityFns.getMarketId
          )
        : new Set();

    // Filter out included feeds before sending to backend
    const marketsToSend = profile.markets.filter((m) => !includedFeedsSet.has(m));

    const toMarketObjects = marketsToSend.map((m) => {
      const dashIndex = m.indexOf("-");
      let group = "",
        market = m,
        reference = undefined;
      if (dashIndex !== -1) {
        group = m.substring(0, dashIndex);
        market = m.substring(dashIndex + 1);
      }
      const provider = findProviderByGroupAndName(
        group,
        market,
        providersForPlatform
      );
      reference = provider?.reference;
      return { group, market, reference };
    });
    localState2 = {
      ...localState2,
      profile: {
        ...localState2.profile,
        agreement: {
          ...localState.profile.agreement,
          marketData: toMarketObjects,
        },
      },
    };
    return { ...localState2 };
  };

  useEffect(() => {
    // Get volume payment data
    const vp = rates.countryrates
      .filter((x) => x.country === "*")
      .sort((a, b) => (a.min < b.min ? -1 : a.min > b.min ? 1 : 0));

    // Clone localState
    let localState2 = { ...localState };

    // Only update rules if they don't exist

    if (
      !profile.agreement?.rules ||
      Object.keys(profile.agreement.rules).length === 0
    ) {
      localState2 = updateProfileWithRates(
        profile.accountType,
        rates,
        localState2
      );
    }

    // Update trading platform and volume payments
    localState2 = updateTradingPlatform(profile, rates, localState2);
    let volumePaymentsData = vp.length > 0 ? vp : [];

    if (tradingPlatform) {
      // If tradingPlatform has shares data, update the corresponding volumePayments entry
      if (tradingPlatform.shares && tradingPlatform.shares.length > 0) {
        const sharesIndex = volumePaymentsData.findIndex(
          (vp) => vp.classifier === SHARES
        );
        if (sharesIndex !== -1) {
          // Update existing shares entry
          volumePaymentsData[sharesIndex] = {
            ...volumePaymentsData[sharesIndex],
            tiers: tradingPlatform.shares,
          };
        } else if (volumePaymentsData.length > 0) {
          // Add new shares entry based on existing entry structure
          volumePaymentsData.push({
            ...volumePaymentsData[0],
            classifier: SHARES,
            tiers: tradingPlatform.shares,
          });
        }
      }

      // If tradingPlatform has payout data, update the corresponding volumePaymentsData entry
      if (tradingPlatform.payout && tradingPlatform.payout.length > 0) {
        const payoutIndex = volumePaymentsData.findIndex(
          (vp) => vp.classifier === PAYOUT
        );
        if (payoutIndex !== -1) {
          // Update existing payout entry
          volumePaymentsData[payoutIndex] = {
            ...volumePaymentsData[payoutIndex],
            tiers: tradingPlatform.payout,
          };
        } else if (volumePaymentsData.length > 0) {
          // Add new payout entry based on existing entry structure
          volumePaymentsData.push({
            ...volumePaymentsData[0],
            classifier: PAYOUT,
            tiers: tradingPlatform.payout,
          });
        }
      }
    }

    localState2.volumePayments = [...volumePaymentsData];

    // Process functions and accumulate updates
    localState2 = calculateTradingPlatform(localState2);
    localState2 = calculateMarketdata(localState2);
    localState2 = calculateRanges("", localState2);
    localState2 = updateMarketDataEntitlements(profile, localState2);
    setupLimits(localState2.profile);

    // Ensure correct market data entitlement is selected for zimtraexpert
    if (profile.accountType === "zimtraexpert") {
      const marketdataprovidersLocal = rates?.marketdataproviders?.filter((p) =>
        p.tradingPlatformRefs.includes(
          utilityFns.findCurrentTradingPlatform(profile, rates)?.reference
        )
      );

      if (marketdataprovidersLocal.length === 1) {
        const marketId = utilityFns.getMarketId(marketdataprovidersLocal[0]);
        localState2.profile.markets = [marketId];
      }
    }

    setLocalState(localState2);
  }, [rates]);

  const handleChangeAccountType = (event, accountTypeName) => {
    let localState2 = { ...localState };
    const accountTypeRef = event.target?.value || accountTypeName;

    localState2 = updateProfileWithRates(accountTypeRef, rates, localState2);
    const TP = rates.tradingplatforms
      .filter((tp) =>
        tp.accountTypeRefs.includes(localState2.profile.accountType)
      )
      .sort((a, b) => a.order - b.order);

    if (TP && TP.length === 1) {
      // For zimtraexpert type
      const newTP = { ...TP[0] };
      localState2.tradingPlatform = newTP;
      localState2.profile.software = newTP.software;
      localState2.profile.softwareType = newTP.softwareType;
    } else if (TP && TP.length > 1) {
      localState2.tradingPlatform = {};
    }

    setupLimits(localState2.profile);

    // Recalculate all dependent values using the strict multiplier logic
    localState2 = calculateRanges("performanceBond", localState2);

    onUpdate(event, "accountType", localState2);
  };

  const handleChange = (event, value) => {
    const localState2 = { ...localState };
    const currentCR = rates.calculationrates.find(
      (r) => r.accountTypeRef === profile.accountType
    );
    const minmax = !!Object.keys(profile.agreement.rules).length;

    // Map local names to backend names
    const nameMapping = {
      initialBuyingPower: "buyingPower",
      maximumDailyLoss: "stopLoss",
      approvedOvernightBuyingPower: "overnightBuyingPower",
      performanceBond: "performanceBond",
    };

    const backendName = nameMapping[value];

    if (minmax) {
      const f = parseFloat(event.target.value);
      const x = Math.max(
        currentCR[backendName].min,
        Math.min(currentCR[backendName].max, f)
      );
      if (!isNaN(x)) localState2.profile.agreement.rules[value] = x;
    } else {
      localState2.profile.agreement.rules[value] = event.target.value;
    }
    onUpdate(event, value, localState2);
  };

  const handleChangeTradingPlatform = (event) => {
    const localState2 = { ...localState };
    const tpIndex = TPRates.findIndex((tp) => {
      return tp.name === event.target.value;
    });
    localState2.profile.marketdataBundle = BASIC;
    localState2.tradingPlatform = tpIndex !== -1 ? TPRates[tpIndex] : {};
    localState2.profile.software = localState2.tradingPlatform?.software;
    localState2.profile.softwareType =
      localState2.tradingPlatform?.softwareType;

    onUpdate(event, "tradingPlatform", localState2);
  };

  const marketdataproviders = useMemo(() => {
    const tradingPlatformReference = tradingPlatform?.reference || "";
    const result = rates?.marketdataproviders?.filter((p) => {
      return p.tradingPlatformRefs.includes(tradingPlatformReference);
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rates, tradingPlatform, localState, profile.accountType]);

  const mutualExclusivityGraph = useMemo(
    () => buildMutualExclusivityGraph(marketdataproviders),
    [marketdataproviders]
  );

  const usedBundles = useMemo(() => {
    const usedBundles = {};
    const usedGroups = {};

    marketdataproviders?.forEach((p) => {
      if (p.group && p.group.length > 0) {
        usedGroups[p.group] = true;
      }
      p.bundle.forEach((b) => {
        if (b && b.length > 0) {
          usedBundles[b] = true;
        }
      });
    });

    return { usedBundles, usedGroups };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketdataproviders, profile.accountType]);

  const calculateMarketdata = (localState2) => {
    if (
      !!Object.keys(usedBundles.usedBundles).length &&
      !usedBundles.usedBundles[localState2.profile.marketdataBundle]
    ) {
      localState2.profile.marketdataBundle = CUSTOM;
    }

    if (localState2.profile.marketdataBundle === CUSTOM) {
      localState2.profile.markets = localState2.profile.markets.filter(
        (mId) => {
          return marketdataproviders.some(
            (m) => utilityFns.getMarketId(m) === mId
          );
        }
      );
    } else {
      const tradingPlatformRef = localState2.tradingPlatform?.reference;
      const marketdataprovidersLocal = rates?.marketdataproviders?.filter((p) =>
        p.tradingPlatformRefs.includes(tradingPlatformRef)
      );

      localState2.profile.markets = marketdataprovidersLocal.reduce((a, m) => {
        if (m.bundle.includes(localState2.profile.marketdataBundle)) {
          a.push(utilityFns.getMarketId(m));
        }
        return a;
      }, []);
      
      // Recursively add all included feeds (via consistsOf) for bundle feeds
      const addIncludedFeedsForMarkets = (markets) => {
        const findByRef = (ref) =>
          marketdataprovidersLocal.find((p) => p.reference === ref);
        const marketsCopy = [...markets];
        marketsCopy.forEach((marketId) => {
          const feed = marketdataprovidersLocal.find((m) => {
            const mId = utilityFns.getMarketId(m);
            return mId === marketId;
          });
          if (feed) {
            addIncludedFeedsToMarkets(
              markets,
              feed,
              findByRef,
              utilityFns.getMarketId
            );
          }
        });
        return markets;
      };
      
      localState2.profile.markets = addIncludedFeedsForMarkets(localState2.profile.markets);
      
      localState2.bundleDescription =
        rates.bundles.find(
          (b) => b.name === localState2.profile.marketdataBundle
        )?.description || "Please select a market data bundle";
      if (localState2.tradingPlatform?.software === "Blackarrow") { // TODO: remove this once we have a proper bundle description
        if (localState2.profile.marketdataBundle === BASIC) {
          localState2.bundleDescription = localState2.tradingPlatform.description;
        } else {
          localState2.bundleDescription = "All Nasdaq quotes, includes full depth of NSDQ feed";
        }
      }
    }

    // Return updated localState2
    localState2 = updateMDEntitlementsWIthBackInfo(profile, localState2);
    return { ...localState2 };
  };

  const bundles = useMemo(() => {
    const filteredMarketdataproviders = marketdataproviders.filter(
      (m) => m.group
    );
    
    const marketdataTotalPrice = rates.bundles
      .map((b) => b.name)
      .reduce((res, c) => {
        res[c] = 0;
        return res;
      }, {});
    
    // For each bundle, calculate price excluding included feeds
    rates.bundles.forEach((bundle) => {
      if (!usedBundles.usedBundles[bundle.name]) {
        return;
      }
      
      // Get all feeds that belong to this bundle
      const bundleFeeds = filteredMarketdataproviders.filter((m) => 
        m.bundle.includes(bundle.name)
      );
      
      const includedFeedsSet = collectBundleIncludedMarketIds(
        bundleFeeds,
        filteredMarketdataproviders,
        utilityFns.getMarketId
      );

      // Only count prices of feeds that are NOT included by other feeds in the bundle
      bundleFeeds.forEach((feed) => {
        const marketId = utilityFns.getMarketId(feed);
        if (!includedFeedsSet.has(marketId)) {
          marketdataTotalPrice[bundle.name] += feed.price;
        }
      });
    });

    const bundles2 = rates.bundles
      .filter((b) => !!usedBundles.usedBundles[b.name])
      .map((b) => ({ ...b, price: marketdataTotalPrice[b.name] }));
    let bundleDescription = "";
    const i = bundles2.findIndex((x) => x.name === profile.marketdataBundle);
    if (i >= 0) {
      bundleDescription = `[${bundles2[i].name}] ${bundles2[i].description}`;
    }

    return { bundles: bundles2, bundleDescription };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketdataproviders, usedBundles, profile.accountType]);

  const calculateTradingPlatform = (state) => {
    if (!state.tradingPlatform) {
      const accountTypeRef = state.profile.accountType;
      const filteredTPs = rates?.tradingplatforms
        .map((tp) => (tp.accountTypeRefs.includes(accountTypeRef) ? tp : null))
        .filter((n) => n);
      state.tradingPlatform = filteredTPs[0];
    }

    // Return updated state
    return { ...state };
  };

  useEffect(() => {
    if (bundles && localState.tradingPlatform && rates) {
      const bundleNamesArray = Object.values(bundles.bundles).map(
        (b) => b.name
      );
      const tradingPlatformRef = localState.tradingPlatform?.reference;
      const marketdataprovidersLocal = rates?.marketdataproviders?.filter(
        (p) => {
          return p.tradingPlatformRefs.includes(tradingPlatformRef);
        }
      );
      const bundlePacks = [];
      bundleNamesArray.forEach((b) => {
        const bundlePack = marketdataprovidersLocal.reduce((a, m) => {
          if (m.bundle.includes(b)) {
            a.push(utilityFns.getMarketId(m));
          }
          return a;
        }, []);
        bundlePacks.push({ bundleName: b, pack: bundlePack });
      });
      setBundlePacks(bundlePacks);
    }
  }, [bundles, localState.tradingPlatform, rates]);

  const onUpdate = (event, value, localState2) => {
    if (event.target?.type === "checkbox") {
      if (event.target.checked) {
      } else {
        localState2.profile.approvedOvernightBuyingPower = 0;
      }
    }
    localState2 = calculateRanges(value, localState2);
    localState2 = calculateMarketdata(localState2);

    setLocalState(localState2);
  };

  const marketAmount = useMemo(() => {
    const includedFeedsSet =
      profile.markets && marketdataproviders
        ? collectIncludedMarketIdsFromSelections(
            profile.markets,
            marketdataproviders,
            utilityFns.getMarketId
          )
        : new Set();

    return marketdataproviders
      .filter((m) => m.group)
      .reduce((a, m) => {
        const marketId = utilityFns.getMarketId(m);
        // Only count price if market is selected AND not included by another feed
        if (profile.markets?.includes(marketId) && !includedFeedsSet.has(marketId)) {
          a += m.price;
        }
        return a;
      }, 0);
  }, [profile.markets, marketdataproviders]);

  const calculateRanges = (value, localState2) => {
    const { rules = {} } = localState2.profile.agreement || {};
    const {
      initialBuyingPower = 0,
      approvedOvernightBuyingPower = 0,
      maximumDailyLoss = 0,
      performanceBond = 0,
    } = rules;

    const parsedRules = {
      initialBuyingPower: parseInt(initialBuyingPower),
      approvedOvernightBuyingPower: parseInt(approvedOvernightBuyingPower),
      maximumDailyLoss: parseInt(maximumDailyLoss),
      performanceBond: parseInt(performanceBond),
    };

    let r = {
      IBP: parsedRules.initialBuyingPower,
      OBP: parsedRules.approvedOvernightBuyingPower,
      MDL: parsedRules.maximumDailyLoss,
      PB: parsedRules.performanceBond,
    };

    switch (value) {
      case "initialBuyingPower":
        RateCalculator.setIBP(r, r.IBP, limits);
        break;
      case "approvedOvernightBuyingPower":
        RateCalculator.setOBP(r, r.OBP, limits);
        break;
      case "maximumDailyLoss":
        RateCalculator.setMDL(r, r.MDL, limits);
        break;
      case "performanceBond":
        RateCalculator.setPB(r, r.PB, limits);
        break;
      case "overnight":
        RateCalculator.setOvernight(r, r.PB, limits);
        break;
      default:
        break;
    }

    // Update localState2.profile.agreement.rules with rounding to nearest hundred
    Object.assign(rules, {
      initialBuyingPower: Math.round(r.IBP / 10) * 10,
      approvedOvernightBuyingPower: Math.round(r.OBP / 10) * 10,
      maximumDailyLoss: Math.round(r.MDL / 10) * 10,
      performanceBond: Math.round(r.PB / 10) * 10,
    });

    // Return updated localState2
    return { ...localState2, limits };
  };

  const changeBundle = () => {
    return (event) => {
      const localState2 = { ...localState };
      localState2.profile.marketdataBundle = event.target.value;

      if (event.target.value === CUSTOM) {
        localState2.bundleDescription = "Custom market data bundle";
      }

      onUpdate(event, "", localState2);
    };
  };

  // return market or undefined
  const findMarketByReference = (ref) => {
    const index = marketdataproviders.findIndex((m) => {
      return m.reference === ref;
    });
    return index !== -1 ? marketdataproviders[index] : undefined;
  };

  const getIncludedFeeds = useMemo(() => {
    if (!profile.markets || !marketdataproviders) {
      return new Set();
    }
    return collectIncludedMarketIdsFromSelections(
      profile.markets,
      marketdataproviders,
      utilityFns.getMarketId
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.markets, marketdataproviders]);

  const isIncludedFeed = (marketId) => {
    return getIncludedFeeds.has(marketId);
  };

  const changeMarket = (marketId, price, dependencyRefs, reference) => {
    return (event) => {
      let localState2 = { ...localState };
      const findByRef = (ref) => findMarketByReference(ref);
      if (event.target.type === "checkbox") {
        if (event.target.checked) {
          if (!localState2.profile.markets?.includes(marketId)) {
            if (reference) {
              removeMutuallyExclusiveSelections(
                localState2.profile.markets,
                reference,
                marketdataproviders,
                utilityFns.getMarketId,
                findByRef,
                mutualExclusivityGraph
              );
            }
            localState2.profile.markets?.push(marketId);
            if (dependencyRefs && dependencyRefs.length > 0) {
              dependencyRefs.forEach((ref) => {
                const m = findMarketByReference(ref);
                if (!m) {
                  return;
                }
                const marketId2 = utilityFns.getMarketId(m);
                if (!localState2.profile.markets.includes(marketId2)) {
                  localState2.profile.markets.push(marketId2);
                }
              });
            }
            const selectedFeed = marketdataproviders.find((m) => {
              const mId = utilityFns.getMarketId(m);
              return mId === marketId;
            });
            if (selectedFeed) {
              addIncludedFeedsToMarkets(
                localState2.profile.markets,
                selectedFeed,
                findByRef,
                utilityFns.getMarketId
              );
            }
          }
        } else {
          removeMarketSelection(
            localState2.profile.markets,
            marketId,
            marketdataproviders,
            utilityFns.getMarketId,
            findByRef
          );
        }

        normalizeConsistsOfPackageSelections(
          localState2.profile.markets,
          marketdataproviders,
          utilityFns.getMarketId,
          findByRef
        );

        const includedFeedsSetForMatching = collectIncludedMarketIdsFromSelections(
          localState2.profile.markets,
          marketdataproviders,
          utilityFns.getMarketId
        );

        const marketsForMatching = localState2.profile.markets.filter(
          (m) => !includedFeedsSetForMatching.has(m)
        );

        const matchedBundle = utilityFns.findMatchingBundle(
          marketsForMatching,
          bundlePacks
        );
        if (matchedBundle) {
          localState2.profile.marketdataBundle = matchedBundle;
          localState2.bundleDescription = bundles?.bundles.find(
            (b) => b.name === matchedBundle
          )?.description;
        } else {
          localState2.profile.marketdataBundle = CUSTOM;
          localState2.bundleDescription = "Custom market data bundle";
        }

        onUpdate(event, "", localState2);
      }
    };
  };

  const setupLimits = (profile) => {
    if (!rates || !rates.calculationrates) {
      return;
    }
    const accountTypeRef =
      profile.accountType || rates.accounttypes[0].reference;
    const currentCR = rates.calculationrates.find(
      (r) => r.accountTypeRef === accountTypeRef
    );

    if (!!currentCR) {
      const { buyingPower, performanceBond, stopLoss, overnightBuyingPower } =
        currentCR;

      // Calculate the desired multiplier for buying power
      const buyingPowerMultiplier =
        (buyingPower.max - buyingPower.min) /
        (performanceBond.max - performanceBond.min);

      // Calculate step sizes to ensure clean multipliers
      const PBStep = performanceBond.step;
      const BPStep = PBStep * buyingPowerMultiplier;

      // Calculate other step sizes proportionally
      const stopLossMultiplier =
        (stopLoss.max - stopLoss.min) /
        (performanceBond.max - performanceBond.min);
      const SLStep = PBStep * stopLossMultiplier;

      const overnightMultiplier =
        (overnightBuyingPower.max - overnightBuyingPower.min) /
        (performanceBond.max - performanceBond.min);
      const OBPStep = PBStep * overnightMultiplier;

      limits.performanceBond = {
        min: parseInt(performanceBond.min),
        default: parseInt(performanceBond.min),
        max: performanceBond.max,
        step: PBStep,
      };
      limits.initialBuyingPower = {
        min: parseInt(buyingPower.min),
        default: parseInt(buyingPower.min),
        max: parseInt(buyingPower.max),
        step: BPStep,
      };
      limits.maximumDailyLoss = {
        min: parseInt(stopLoss.min),
        default: parseInt(stopLoss.min),
        max: parseInt(stopLoss.max),
        step: SLStep,
      };
      limits.approvedOvernightBuyingPower = {
        min: parseInt(overnightBuyingPower.min),
        default: parseInt(overnightBuyingPower.min),
        max: parseInt(overnightBuyingPower.max),
        step: OBPStep,
      };
    }
  };

  const generateSliders = () => {
    const accountType = profile.accountType;
    const tooltips = tooltipTexts[accountType] || {};

    return (
      <div className="sliders">
        <RangeValue
          id="refundable-performance-bond-slider"
          placeholder="Refundable Performance Bond"
          name="performanceBond"
          key="performanceBond"
          disabled={!!!profile.accountType}
          placeholdervalue={utilityFns.money(
            profile.agreement.rules?.performanceBond || 0,
            0
          )}
          min={limits.performanceBond.min}
          step={limits.performanceBond.step}
          max={limits.performanceBond.max}
          minmaxformatter={utilityFns.moneyshort}
          value={
            profile.agreement.rules?.performanceBond ||
            limits.performanceBond.min
          }
          onChange={(e) => handleChange(e, "performanceBond")}
          required
        />
        {/* Display other values as editable inputs */}
        <div className="rates-display">
          <RangeValue
            id="day-buying-power-input"
            placeholder="Daily Buying Power"
            infoText={tooltips.dailyBuyingPower}
            name="initialBuyingPower"
            disabled={!!!profile.accountType}
            placeholdervalue={utilityFns.money(
              profile.agreement.rules?.initialBuyingPower || 0,
              0
            )}
            min={limits.initialBuyingPower.min}
            max={limits.initialBuyingPower.max}
            value={
              profile.agreement.rules?.initialBuyingPower ||
              limits.initialBuyingPower.min
            }
            onChange={(e) => handleChange(e, "initialBuyingPower")}
            hideSlider={true}
            required
          />
          <RangeValue
            id="daily-stop-loss-input"
            placeholder="Daily Stop Loss"
            name="maximumDailyLoss"
            disabled={!!!profile.accountType}
            placeholdervalue={utilityFns.money(
              profile.agreement.rules?.maximumDailyLoss || 0,
              0
            )}
            min={limits.maximumDailyLoss.min}
            max={limits.maximumDailyLoss.max}
            value={
              profile.agreement.rules?.maximumDailyLoss ||
              limits.maximumDailyLoss.min
            }
            onChange={(e) => handleChange(e, "maximumDailyLoss")}
            hideSlider={true}
            required
          />
          <RangeValue
            id="overnight-buying-power-input"
            placeholder="Overnight Buying Power"
            infoText={tooltips.approvedOvernightBuyingPower}
            name="approvedOvernightBuyingPower"
            disabled={!!!profile.accountType}
            placeholdervalue={utilityFns.money(
              profile.agreement.rules?.approvedOvernightBuyingPower || 0,
              0
            )}
            min={limits.approvedOvernightBuyingPower.min}
            max={limits.approvedOvernightBuyingPower.max}
            value={
              profile.agreement.rules?.approvedOvernightBuyingPower ||
              limits.approvedOvernightBuyingPower.min
            }
            onChange={(e) => handleChange(e, "approvedOvernightBuyingPower")}
            hideSlider={true}
            required
          />
        </div>
      </div>
    );
  };

  const getTiers = (forPayout) => {
    if (volumePayments.length > 0) {
      const filterdVP = volumePayments.filter(
        (vp) => vp.accountTypeRef === profile.accountType
      );
      console.log("filterdVP", filterdVP);
      const tiers = filterdVP.find(
        (i) => i.classifier === (forPayout ? PAYOUT : SHARES)
      )?.tiers;
      return tiers;
    }
  };

  const generateTradingPlatform = () => {
    const platformOptions = rates.tradingplatforms
      .filter((tp) => tp.accountTypeRefs.includes(profile.accountType))
      .sort((a, b) => a.order - b.order)
      .map((t) => ({
        value: t.name,
        text: t.name,
      }));

    return (
      <div className="platform-selector">
        <ToggleButtonGroup
          id="trading-platform-toggle"
          noneoption="Trading platform"
          placeholder="Select platform"
          options={platformOptions}
          name="tradingplatform"
          value={tradingPlatform?.name || ""}
          onChange={(e) => handleChangeTradingPlatform(e)}
          required
        />
      </div>
    );
  };

  const generateTradingPlatformText = () => {
    const isBlackarrow = tradingPlatform?.software === "Blackarrow"; // TODO: remove this after end of Blackarrow campaign
    const isBlackarrowIntermediate = profile.accountType === "zimtraexpert";

    if (profile?.accountType) {
      let foundPrice = null;
      if (tradingPlatform?.software === "Blackarrow") {
        foundPrice = `$${tradingPlatform.price}`;
      } else {
        getTiers(false)?.forEach((tier) => {
          const tv = generateTierValue(tier, ["software"]);
          if (tv && tv.value && Number(tv.value.substring(1)) > 0) {
            foundPrice = tv.value;
          }
        });
      }
      if (
        profile.accountType === "zimtraexpert" &&
        profile.software === "Sterling"
      ) {
        return (
          <p className="traders-fee">
            Trader's software fee is free.
            <br />
            Zimtra Intermediate monthly subscription is{" "}
            <strong>free for the first month</strong> and depends on traded
            volume afterward.
          </p>
        );
      } else {
        return (
          <div className="traders-fee-wrapper">
            <p className="traders-fee-centered">
              <span>Monthly Software Fee: &nbsp;</span>
              <strong>{foundPrice}</strong>
            </p>
            {isBlackarrow && (
              <p className="traders-fee-centered">
                <i>Free for the first month</i><br />
                {isBlackarrowIntermediate &&
                  "After first month, trade more than 250k shares ≥$1 a month to maintain."}
              </p>
            )}
          </div>
        );
      }
    }
  };

  const generateMarketdataDependency = () => {
    const ref2name = {};
    marketdataproviders
      .filter((m) => !!m.reference)
      .forEach((m) => {
        ref2name[m.reference] = m.name;
      });

    return marketdataproviders
      .filter((m1) => m1.dependencyRefs && m1.dependencyRefs.length > 0)
      .map((m) => {
        return (
          <p className="marketdata-dependency-item" key={m._id}>
            <span className="marketdata-dependency-item-name">
              &#42; {m.name}
            </span>
            &nbsp;requires&nbsp;
            <span className="marketdata-dependency-item-deps">
              {m.dependencyRefs.map((r) => ref2name[r]).join(",")}
            </span>
          </p>
        );
      });
  };

  const marketdataSubscriptions = useMemo(() => {
    const mds = bundles
      ? bundles.bundles.map((b) => {
          return {
            value: b.name,
            text: `${b.name} ${utilityFns.money(b.price, 2)}/month`,
          };
        })
      : [];

    // Add custom option with conditional text
    if (bundles.bundles.length > 1) {
      mds.push({
        value: CUSTOM,
        text:
          profile.marketdataBundle === CUSTOM
            ? `Custom ${utilityFns.money(marketAmount, 2)}/month`
            : "Custom $0.00/month",
        disabled: profile.marketdataBundle !== CUSTOM,
      });
    }

    return mds;
  }, [marketAmount, bundles, profile.marketdataBundle]);

  const generateMarketdataSelector = () => {
    return (
      <ToggleButtonGroup
        name="marketdataBundle"
        id="marketdata-dropdown"
        placeholder="Select market data"
        resetKey={`${profile.accountType}-${tradingPlatform?.name}`}
        options={marketdataSubscriptions}
        value={profile.marketdataBundle}
        onChange={changeBundle()}
      />
    );
  };

  const generateMarketdataSubscription = () => {
    const { groups } = rates;

    const groupMaxOrders = groups.reduce((acc, group) => {
      const maxOrder = Math.max(
        ...marketdataproviders
          .filter((x) => x.group === group)
          .map((x) => x.order)
      );
      acc[group] = maxOrder;
      return acc;
    }, {});

    const sortedGroups = groups
      .filter((x) => !!usedBundles.usedGroups[x])
      .sort((a, b) => groupMaxOrders[b] - groupMaxOrders[a]);

    return (
      <div className="marketdata-groups">
        {sortedGroups.map((g) => {
          return (
            <div className="marketdata-group" key={g}>
              <div className="name">{g}</div>
              <div className="marketdata-group-items">
                {marketdataproviders
                  ?.filter((x) => x.group === g)
                  .sort((a, b) => b.order - a.order)
                  .map((m) => {
                    const marketId = utilityFns.getMarketId(m);
                    const v = profile.markets?.includes(marketId);
                    const isIncluded = isIncludedFeed(marketId);
                    // Backend may persist only the parent (e.g. Level 1 Package); children are implied
                    // via consistsOf. Treat included rows as checked for display (same as after toggling).
                    const isChecked =
                      marketdataproviders.length === 1 ? true : v || isIncluded;
                    const hardDisabled = marketdataproviders.length <= 2;
                    const exclusiveBlocked = isExclusiveBlockedForFeed(
                      m.reference,
                      marketId,
                      profile.markets || [],
                      marketdataproviders,
                      utilityFns.getMarketId,
                      mutualExclusivityGraph
                    );
                    const checkboxDisabled = hardDisabled || isIncluded;
                    const namesuffix =
                      m.dependencyRefs && m.dependencyRefs.length > 0 ? (
                        <span>{m.name} &#42;</span>
                      ) : (
                        <span>{m.name}</span>
                      );

                    return (
                      <div
                        className={`marketdata-item ${isIncluded ? "included-feed" : ""}${
                          exclusiveBlocked ? " exclusive-blocked" : ""
                        }`}
                        key={m._id}
                      >
                        <div className="name">
                          <Checkbox
                            disabled={checkboxDisabled}
                            appearanceDisabled={exclusiveBlocked && !hardDisabled}
                            placeholder={namesuffix}
                            value={isChecked}
                            onChange={changeMarket(
                              marketId,
                              m.price,
                              m.dependencyRefs,
                              m.reference
                            )}
                          />
                        </div>
                        <div
                          className={`price ${v || isIncluded ? "selected" : ""} ${isIncluded ? "crossed-out" : ""}`}
                        >
                          {utilityFns.money(m.price, 2)}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          );
        })}
        <div className="marketdata-dependency-items">
          {generateMarketdataDependency()}
        </div>
      </div>
    );
  };

  const formatValue = (v, formats) => {
    if (formats && formats.length > 0) {
      switch (formats[0]) {
        case "money":
          const digits = formats[1] || 0;
          return utilityFns.money(v, digits);
        default:
      }
    }
    return v;
  };

  const generateTierValue = (t, spec) => {
    let value = null;
    switch (spec[0]) {
      case "commission":
        value = t.commission;
        break;
      case "payout":
        value = `${t.payout * 100.0}%`;
        break;
      case "overnight":
        value = `${t.interest.fed ? "FED+" : ""}${t.interest.percent.toFixed(
          2
        )}%`;
        break;
      case "software":
        const tpPrice = utilityFns.calculatePlatformPriceWithConditions(
          tradingPlatform,
          profile
        );
        let softwareprice = "platform?";
        if (typeof tpPrice !== "undefined") {
          const calculatedPrice = t.software.base
            ? tpPrice + t.software.change
            : t.software.change;

          const adjustedPrice = Math.max(calculatedPrice, 0);
          softwareprice = `$${adjustedPrice}`;
        }
        value = softwareprice;
        break;
      default:
        value = t[spec[0]];
    }

    return {
      className: spec[1] || spec[0],
      value: formatValue(value, spec.slice(2)),
    };
  };

  const renderScheduleA = () => {
    const formatAccType = (type) => {
      if (type === "zimtrapro") return "Zimtra Pro";
      if (type === "zimtraelite") return "Zimtra Elite";
      if (type === "zimtraexpert") return "Zimtra Intermediate";
    };

    return (
      <div className="schedule-a">
        <h4>SUMMARY</h4>
        <div className="schedule-a-content">
          <div className="schedule-a-content-item">
            <span>Account Type:</span>
            <span className="font-bold">
              {formatAccType(profile.accountType)}
            </span>
          </div>
          <div className="schedule-a-content-item">
            <span>Refundable Performance Bond:</span>
            <span className="font-bold">
              {formatValue(profile.agreement.rules?.performanceBond, [
                "money",
                "2",
              ])}
            </span>
          </div>
          <div className="schedule-a-content-item">
            <span>Day Buying Power:</span>
            <span className="font-bold">
              {formatValue(profile.agreement.rules?.initialBuyingPower, [
                "money",
                "2",
              ])}
            </span>
          </div>
          <div className="schedule-a-content-item">
            <span>Daily Stop Loss:</span>
            <span className="font-bold">
              {formatValue(profile.agreement.rules?.maximumDailyLoss, [
                "money",
                "2",
              ])}
            </span>
          </div>
          <div className="schedule-a-content-item">
            <span>Approved Overnight Buying Power:</span>
            <span className="font-bold">
              {formatValue(
                profile.agreement.rules?.approvedOvernightBuyingPower,
                ["money", "2"]
              )}
            </span>
          </div>
          <div className="schedule-a-content-item">
            <span>Trading platform:</span>
            <span className="font-bold">
              {localState.tradingPlatform?.name}
            </span>
          </div>
          <div className="schedule-a-content-item">
            <span>Market data subscription option:</span>
            <span className="font-bold">
              {profile.marketdataBundle}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="business-deal-wrapper">
      <div className="business-deal-title-wrapper">
        <h4 className="business-deal-title" id="businessdeal">
          Business Deal
        </h4>
        <span className="business-deal-step-number">
          step {stepsData.step} of {stepsData.totalSteps}
        </span>
      </div>
      <ContinueOnDevice />
      <p className="header-text">Set up your business deal.</p>
      <hr />
      <div className="business-deal-wrapper-inner">
        <div className="business-deal-wrapper-inner-left">
          <h4>Deal Type</h4>
          <span>
            Please use the following sliders to determine your capital and risk
            settings requirements.
          </span>
        </div>
        <div className="account-type-wrapper">
          <ToggleButtonGroup
            id="business-deal-account-type-dropdown"
            name="accountType"
            placeholder="Select type"
            value={profile.accountType || ""}
            options={
              rates.accounttypes.map((s) => ({
                value: s.reference,
                text: s.name,
              })) || []
            }
            onChange={(e) => handleChangeAccountType(e, "accountType")}
            required
          />
          <div>{generateSliders()}</div>
        </div>
      </div>
      <hr />
      <div className="trading-platform-wrapper-inner">
        <div className="trading-platform-wrapper-inner-left">
          <h4>Trading Platform</h4>
          {generateTradingPlatformText()}
        </div>
        <div className="trading-platform-wrapper-inner-right">
          {generateTradingPlatform()}
        </div>
      </div>
      <hr />
      <div className="market-data-wrapper-inner">
        <div className="market-data-wrapper-inner-left">
          <h4>Market Data</h4>
          <span className="traders-fee bundle-description">
            {bundleDescription}
          </span>
        </div>
        <div className="market-data-wrapper-inner-right">
          {generateMarketdataSelector()}
          {generateMarketdataSubscription()}
        </div>
      </div>
      <hr />
      {renderScheduleA()}
      <hr />
    </div>
  );
};

export default BusinessDealStep;
